import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  output,
  signal,
} from '@angular/core';
import { X, RotateCcw, ChevronLeft, ChevronRight, LucideIconData } from 'lucide-angular';
import { ProductVariant } from '../../models/product.model';
import { IconComponent } from '../icon/icon.component';

/**
 * Lightbox-galería: abre un modal con la imagen en grande, permite navegar
 * entre todas las del producto (prev/next, dots, swipe, flechas del teclado)
 * y soporta flip 3D para cartas double-face (aplica sólo a la primera imagen,
 * que es la cara principal en el modelo del backend).
 *
 * No lleva acciones de compra — eso sigue siendo del ProductZoomModal (quick-view).
 */
@Component({
  selector: 'app-image-lightbox',
  imports: [IconComponent],
  templateUrl: './image-lightbox.component.html',
  styleUrl: './image-lightbox.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'contents',
    '(document:keydown.escape)': 'onClose()',
    '(document:keydown.arrowLeft)': 'prev()',
    '(document:keydown.arrowRight)': 'next()',
  },
})
export class ImageLightboxComponent {
  images = input.required<string[]>();
  alt = input.required<string>();
  initialIndex = input(0);
  backImageUrl = input<string | undefined>(undefined);
  variant = input<ProductVariant>('non-foil');

  close = output<void>();

  protected readonly closeIcon: LucideIconData = X;
  protected readonly rotateIcon: LucideIconData = RotateCcw;
  protected readonly prevIcon: LucideIconData = ChevronLeft;
  protected readonly nextIcon: LucideIconData = ChevronRight;

  protected readonly currentIndex = signal(0);
  protected readonly isFlipped = signal(false);

  protected readonly totalImages = computed(() => this.images().length);
  protected readonly currentImage = computed(
    () => this.images()[this.currentIndex()] ?? '',
  );
  protected readonly hasMultiple = computed(() => this.totalImages() > 1);
  /** La cara posterior vive sólo sobre la imagen "principal" (índice 0). */
  protected readonly flipAvailable = computed(
    () => !!this.backImageUrl() && this.currentIndex() === 0,
  );

  private touchStartX = 0;

  constructor() {
    // Inicializa el índice al abrir — respeta la imagen que el usuario
    // tenía en el slot grande.
    effect(() => {
      const i = this.initialIndex();
      const total = this.images().length;
      this.currentIndex.set(total > 0 ? Math.min(Math.max(0, i), total - 1) : 0);
      this.isFlipped.set(false);
    });
  }

  protected prev(): void {
    if (!this.hasMultiple()) return;
    const i = this.currentIndex();
    this.currentIndex.set(i === 0 ? this.totalImages() - 1 : i - 1);
    this.isFlipped.set(false);
  }

  protected next(): void {
    if (!this.hasMultiple()) return;
    const i = this.currentIndex();
    this.currentIndex.set(i === this.totalImages() - 1 ? 0 : i + 1);
    this.isFlipped.set(false);
  }

  protected goTo(index: number): void {
    if (index < 0 || index >= this.totalImages()) return;
    this.currentIndex.set(index);
    this.isFlipped.set(false);
  }

  protected toggleFlip(): void {
    if (!this.flipAvailable()) return;
    this.isFlipped.update((v) => !v);
  }

  protected onClose(): void {
    this.close.emit();
  }

  protected onBackdropClick(event: Event): void {
    if (event.target === event.currentTarget) this.onClose();
  }

  protected onTouchStart(event: TouchEvent): void {
    this.touchStartX = event.touches[0].clientX;
  }

  protected onTouchEnd(event: TouchEvent): void {
    if (!this.hasMultiple()) return;
    const delta = event.changedTouches[0].clientX - this.touchStartX;
    const threshold = 50;
    if (Math.abs(delta) < threshold) return;
    if (delta < 0) this.next();
    else this.prev();
  }
}
